import React from 'react'
import { useHistory } from 'react-router-dom'
import { Box, Button, Grid, Paper, Switch, Tooltip, Typography } from '@mui/material'
import { AddBox, StartOutlined } from '@mui/icons-material'
import { RtkQueryHelper } from '../../../api-services/shared/RtkQueryHelper'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { formatDateStringAsApiFormat } from '../../../utility/formatters/DateFormatter'
import LoadingComponent from '../../components/ui-widgets/LoadingComponent'
import ErrorComponent from '../../components/ui-widgets/ErrorComponent'
import { useGetProjectsQuery } from '../../../api-services/assets/projectApi'
import {
  selectCurrentUrlSlug,
  selectDataGridPage,
  selectShowInactiveProjects,
  setDataGridPage,
  setShowInactiveProjects
} from './editSolarProjectSlice'
import { DataGrid, type GridColDef, type GridRenderCellParams, type GridValueGetterParams } from '@mui/x-data-grid'
import { ProjectStatusChip } from '../../components/chips/ProjectStatusChip'
import ProjectCrowdsaleStatusChip from '../../components/chips/ProjectCrowdsaleStatusChip'

const SolarProjectsPage: React.FC = () => {
  const history = useHistory()
  const dispatch = useAppDispatch()

  const currentUrlSlug = useAppSelector<string | null>(selectCurrentUrlSlug)
  const page: number = useAppSelector<number>(selectDataGridPage)
  const showInactiveProjects: boolean = useAppSelector<boolean>(selectShowInactiveProjects)

  const getProjectsApiResult = useGetProjectsQuery({ showInactiveProjects })

  const goTo = (location: string): void => {
    history.push(location)
  }

  const handleGoToEdit = (projectUrlSlug: string | undefined): void => {
    goTo(`/app/projects/${projectUrlSlug}/edit`)
  }

  const handleChangePage = (newPage: number): void => {
    dispatch(setDataGridPage(newPage))
  }

  const handleProjectFilterSwitch = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (!event.target.checked) {
      dispatch(setShowInactiveProjects(false))
    } else if (event.target.checked) {
      dispatch(setShowInactiveProjects(true))
    }
  }

  const getEditId = (params: GridValueGetterParams): string => {
    return params.row.urlSlug
  }

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 35, editable: false },
    { field: 'name', headerName: 'Project name', flex: 60, editable: false },
    { field: 'solarProjectId', headerName: 'Solar Project ID', width: 50, editable: false },
    { field: 'urlSlug', headerName: 'URL Slug', flex: 50, editable: false },
    {
      field: 'projectStatus',
      headerName: 'Status',
      flex: 40,
      renderCell: params => (
        <ProjectStatusChip projectStatus={params.row.projectStatus}/>
      )
    },
    {
      field: 'projectCrowdsaleStatus',
      headerName: 'Crowdsale Status',
      flex: 40,
      renderCell: params => (
        <ProjectCrowdsaleStatusChip projectCrowdsaleStatus={params.row.projectCrowdsaleStatus}/>
      )
    },
    {
      field: 'crowdsaleEndDate',
      headerName: 'Crowdsale End Date',
      flex: 40,
      renderCell: params => (
        <Tooltip title={params.row.crowdsaleEndDate !== '' ? 'Crowdsale End Date' : (params.row.campaignEnds !== '' ? 'Campaign Ends' : '')}>
          <span>{formatDateStringAsApiFormat(params.row.crowdsaleEndDate !== '' ? params.row.crowdsaleEndDate : params.row.campaignEnds)}</span>
        </Tooltip>
      ),
      renderHeader: () => (
        <Tooltip title="If Crowdsale End Date exists, then we use that, otherwise we use Campaign Ends">
          <span className={'MuiDataGrid-columnHeaderTitle'}>Crowdsale End Date</span>
        </Tooltip>
      )
    },
    {
      field: 'earlyEnergyDate',
      headerName: 'Early Energy Date',
      flex: 30,
      renderCell: params => (
        <span>{formatDateStringAsApiFormat(params.row.earlyEnergyDate)}</span>
      )
    },
    {
      field: 'assetLeaseCommercialOperationDate',
      headerName: 'Asset Lease COD',
      flex: 30,
      renderCell: params => (
        <span>{formatDateStringAsApiFormat(params.row.assetLeaseCommercialOperationDate)}</span>
      )
    },
    {
      field: 'numberOfCellOwnerAgreements',
      headerName: '# COAs',
      flex: 25,
      renderCell: params => (
        <span>{params.row.numberOfCellOwnerAgreements ?? '-'}</span>
      )
    },
    {
      field: 'coaTemplateVersion',
      headerName: 'COA version',
      flex: 20,
      renderCell: params => (
        <span>{params.row.coaTemplateVersion ?? '-'}</span>
      )
    },
    { field: 'numberOfCellOwners', headerName: '# Cell Owners', flex: 30 },
    {
      field: 'editSolarPageId', // Don't repeat ID - does weird things with the ordering of the columns
      headerName: '',
      width: 100,
      valueGetter: getEditId,
      renderCell: (params: GridRenderCellParams<string>) => (
        <Button onClick={() => { handleGoToEdit(params.value) }} type={'button'} variant={'text'}>
          <StartOutlined />
        </Button>
      )
    }
  ]

  return (
    <React.Fragment>
      <Grid container>
        <Grid item lg={12}>
          <Grid container sx={{ mb: 4 }} spacing={3}>
            <Grid item lg={6}>
              <Typography variant={'h4'}>Solar Projects</Typography>
            </Grid>
            <Grid item container lg={6} spacing={2} direction="row" alignItems="right" justifyContent="right">
              <Grid item>
                <Button variant={'contained'} onClick={() => { goTo('/app/projects/create') }}>
                  <AddBox />
                  Create
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Paper>
            <Box sx={{ p: 4, mt: 3 }}>
              { getProjectsApiResult.isLoading && <LoadingComponent />}
              { getProjectsApiResult.isError && <ErrorComponent message={RtkQueryHelper.mapResponseError(getProjectsApiResult.error)} />}
              { getProjectsApiResult.isSuccess && (
                <DataGrid
                  rows={getProjectsApiResult.data}
                  columns={columns}
                  autoHeight={true}
                  disableSelectionOnClick
                  getRowClassName={params => {
                    return currentUrlSlug === params.row.urlSlug ? 'Mui-selected' : ''
                  }}

                  pagination
                  pageSize={15}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPageOptions={[15]}
                />
              )}
            </Box>

            <Box sx={{ pr: 4, pb: 4, pt: 0, textAlign: 'right' }}>
              Show Inactive Projects
              <Switch
                checked={showInactiveProjects}
                onChange={handleProjectFilterSwitch}
                color="primary"
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </React.Fragment>
  )
}

export default SolarProjectsPage
